import { useState, useEffect, useRef } from "react";
import AuthService from "../services/auth.service";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import FormAction from "./FormAction";

export default function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  const otpInputRef = useRef(null);

  const [step, setStep] = useState("phone"); // "phone" | "otp"
  const [cellphone, setCellphone] = useState("");
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    const currentUser = AuthService.getCurrentUser();
    if (currentUser) {
      if (jwtDecode(currentUser["token"]).exp > Date.now() / 1000) {
        navigate("/parcels");
      }
    }
  }, []);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!cellphone.trim()) return;
    setError("");
    setIsLoading(true);
    try {
      await AuthService.requestOtp(cellphone.trim());
      setStep("otp");
      setResendCooldown(60);
      setTimeout(() => otpInputRef.current?.focus(), 100);
    } catch (err) {
      setError(
        err?.response?.data?.message || "Failed to send OTP. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setError("");
    setIsLoading(true);
    try {
      await AuthService.verifyOtp(cellphone.trim(), code.trim());
      if (location?.state?.redirect_to !== undefined) {
        // Add a flag to indicate user just logged in
        const stateWithLoginFlag = {
          ...location.state,
          from_login: true,
        };
        navigate(location.state.redirect_to, { state: stateWithLoginFlag });
      } else {
        window.location.replace("/parcels");
      }
    } catch (err) {
      setError(
        err?.response?.data?.message || "Invalid OTP. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError("");
    setIsLoading(true);
    try {
      await AuthService.requestOtp(cellphone.trim());
      setResendCooldown(60);
      setCode("");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to resend OTP.");
    } finally {
      setIsLoading(false);
    }
  };

  const redirectToSignup = () => {
    if (location?.state?.redirect_to !== undefined) {
      navigate("/signup", { state: location.state });
    } else {
      window.location.replace("/signup");
    }
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      {step === "phone" && (
        <>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Sign in</h2>
            <p className="mt-1 text-sm text-gray-500">
              Enter your cell number to receive a one-time PIN.
            </p>
          </div>

          {error && (
            <div
              className="p-4 text-sm text-red-800 rounded-lg bg-red-50"
              role="alert"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleRequestOtp} className="flex flex-col gap-4">
            <div>
              <label
                htmlFor="cellphone"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Cell number
              </label>
              <input
                id="cellphone"
                type="tel"
                required
                value={cellphone}
                onChange={(e) => setCellphone(e.target.value)}
                placeholder="e.g. 0821234567"
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
            </div>
            <FormAction
              handleSubmit={handleRequestOtp}
              loading={isLoading}
              text="Send OTP"
            />
          </form>

          <div className="text-center flex gap-2 justify-center">
            <span className="text-black text-base font-medium">
              Don't have an account?
            </span>
            <span
              className="text-purple-600 text-base font-medium cursor-pointer"
              onClick={redirectToSignup}
            >
              Sign Up
            </span>
          </div>
        </>
      )}

      {step === "otp" && (
        <>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Enter OTP</h2>
            <p className="mt-1 text-sm text-gray-500">
              We sent a one-time PIN to{" "}
              <span className="font-medium text-gray-700">{cellphone}</span>.
            </p>
          </div>

          {error && (
            <div
              className="p-4 text-sm text-red-800 rounded-lg bg-red-50"
              role="alert"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleVerifyOtp} className="flex flex-col gap-4">
            <div>
              <label
                htmlFor="otp"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                One-time PIN
              </label>
              <input
                ref={otpInputRef}
                id="otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 tracking-widest text-center text-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
            </div>
            <FormAction
              handleSubmit={handleVerifyOtp}
              loading={isLoading}
              text="Verify & Sign in"
            />
          </form>

          <div className="text-center flex gap-2 justify-center text-sm">
            <span className="text-gray-500">Didn't receive it?</span>
            <button
              type="button"
              onClick={handleResend}
              disabled={resendCooldown > 0 || isLoading}
              className={`font-medium ${resendCooldown > 0 ? "text-gray-400 cursor-default" : "text-purple-600 cursor-pointer hover:text-purple-700"}`}
            >
              {resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : "Resend OTP"}
            </button>
          </div>

          <button
            type="button"
            onClick={() => {
              setStep("phone");
              setError("");
              setCode("");
            }}
            className="text-center text-sm text-gray-400 hover:text-gray-600"
          >
            ← Change number
          </button>
        </>
      )}
    </div>
  );
}
